import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useParams } from "react-router-dom"

import FilterPagination from 'pages/parts/FilterPagination'
import Rightbar from '../cheques/Rightbar'
import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney } from 'helpers/helpers'

function Index({ modal, clientId }) {
	const { t } = useTranslation()
	const { client_id } = useParams()
	const resolvedId = modal ? clientId : client_id

	const [card, setCard] = useState(null)
	const [history, setHistory] = useState({ data: [], total: 0 })
	const [filter, setFilter] = useState({ page: 0, size: 20 })
	const [pagination, setPagination] = useState({ url: `/report-client-unified/${resolvedId}`, response: null })
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})

	async function loadCard() {
		const response = await GET(`/report-client-card/${resolvedId}`)
		if (httpOk(response)) setCard(response.data)
	}

	async function loadHistory() {
		const response = await GET(`/report-client-unified/${resolvedId}`, filter, { loader: true })
		if (httpOk(response)) {
			setHistory(response.data)
			setPagination(prev => ({ ...prev, response: response.data }))
		}
	}

	async function toggleRightbar(bool, chequeId) {
		if (bool && chequeId) {
			const response = await GET(`/cheques/${chequeId}`, {}, { loader: true })
			if (httpOk(response)) {
				setSelectedItem(response.data)
				setShowRightBar(true)
			}
		} else {
			setShowRightBar(false)
			setSelectedItem({})
		}
	}

	function getRowLabel(item) {
		switch (item.row_type) {
			case 'cash':          return { label: t('cash_sale'),        bg: '#cfe2ff', color: '#084298' }
			case 'terminal':      return { label: 'Terminal',            bg: '#cff4fc', color: '#055160' }
			case 'sold_on_credit':return { label: t('sold_on_credit'),   bg: '#f8d7da', color: '#842029' }
			case 'payment':       return { label: t('debt_paid'),        bg: '#d1e7dd', color: '#0f5132' }
			case 'return':        return { label: t('return'),           bg: '#fff3cd', color: '#664d03' }
			case 'outflow':       return { label: t('out_balance'),      bg: '#e2e3e5', color: '#41464b' }
			default:              return { label: item.row_type,         bg: '#e2e3e5', color: '#41464b' }
		}
	}

	function getAmountCell(item) {
		switch (item.row_type) {
			case 'cash':
			case 'terminal':
				return <span className="text-primary">{formatMoney(item.amount)}</span>
			case 'sold_on_credit':
				// Sotuv — savdo qiymati (musbat); qarz "Карз" ustunida ko'rinadi
				return <span className="text-primary">{formatMoney(item.amount)}</span>
			case 'outflow':
				return <span className="text-danger">- {formatMoney(item.amount)}</span>
			case 'return':
				// Qaytarish — savdoni KAMAYTIRADI (teskari sotuv), shuning uchun MINUS.
				// Shunda "Савдо" ustuni yig'indisi sof aylanmani beradi: sotuv − qaytarish.
				return <span className="text-danger">- {formatMoney(item.amount)}</span>
			case 'payment':
				// Qarz qaytarildi — savdo emas; summa "Берилган" ustunida ko'rsatiladi
				return <span className="text-muted">—</span>
			default:
				return <span className="text-muted">{formatMoney(item.amount)}</span>
		}
	}

	useEffect(() => {
		loadCard()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		loadHistory()
	}, [filter.page, filter.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* Page header */}
			<div className={`page-header ${modal ? 'modal-page' : ''}`}>
				<div className="d-flex align-items-center flex-wrap gap-2">
					<h4 className="mb-0">{t('client_card')}</h4>
					<i className="uil uil-angle-double-right fz-20 text-muted"></i>
					<h6 className="mb-0" title={card?.client_id}>{card?.name || '...'}</h6>
					{card?.client_type_name &&
						<span className="badge bg-secondary">{card.client_type_name}</span>
					}
					{card?.phone &&
						<span className="text-muted fz-13">
							<i className="uil uil-phone me-1"></i>{card.phone}
						</span>
					}
				</div>
			</div>

			{/* Stats cards — 3 rows x 4 cols */}
			{card && (() => {
				const debt       = Number(card.current_debt ?? 0)
				const cashAmt    = Number(card.cash_amount ?? 0)
				const creditAmt  = Number(card.credit_amount ?? 0)
				const total      = cashAmt + creditAmt
				const cashPct    = total > 0 ? Math.round(cashAmt / total * 100) : 0
				const creditPct  = 100 - cashPct
				const debtAge    = card.debt_age_days
				const debtColor  = debtAge == null ? 'text-muted'
				                 : debtAge <= 30   ? 'text-warning'
				                 : 'text-danger'
				const StatCard = ({ label, value, color = 'text-dark', sub }) => (
					<div className="col-6 col-md-3">
						<div className="card h-100">
							<div className="card-body text-center py-3 px-2">
								<div className="text-muted fz-11 mb-1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
								<div className={`fw-bold fs-6 ${color}`}>{value}</div>
								{sub && <div className="text-muted fz-11 mt-1">{sub}</div>}
							</div>
						</div>
					</div>
				)
				return (
					<>
						{/* Row 1: Financial */}
						<div className="row g-2 mb-2">
							<StatCard label={t('total_sales')}  color="text-primary"
								value={formatMoney(card.total_sales_amount)} />
							<StatCard label={t('current_debt')}
								color={debt < 0 ? 'text-danger' : 'text-success'}
								value={formatMoney(debt)}
								sub={debt < 0 ? null : t('no_debt')} />
							<StatCard label={t('total_paid')}   color="text-success"
								value={formatMoney(card.total_paid)} />
							<StatCard label={t('avg_cheque')}   color="text-info"
								value={formatMoney(card.avg_cheque_amount)} />
						</div>

						{/* Row 2: Sales analytics */}
						<div className="row g-2 mb-2">
							<StatCard label={t('cheques_count')} color="text-secondary"
								value={`${card.total_cheques ?? 0} ${t('count_short')}`} />
							<StatCard label={t('max_cheque')}   color="text-warning"
								value={formatMoney(card.max_cheque_amount)} />
							<StatCard label={t('returns_count')}
								color={(card.returns_count ?? 0) > 0 ? 'text-danger' : 'text-muted'}
								value={`${card.returns_count ?? 0} ${t('count_short')}`} />
							<div className="col-6 col-md-3">
								<div className="card h-100">
									<div className="card-body text-center py-3 px-2">
										<div className="text-muted fz-11 mb-2">{t('cash_credit_ratio')}</div>
										{total > 0 ? (
											<>
												<div className="progress mb-1" style={{ height: '6px', borderRadius: '4px' }}>
													<div className="progress-bar bg-primary" style={{ width: `${cashPct}%` }}></div>
													<div className="progress-bar bg-danger"  style={{ width: `${creditPct}%` }}></div>
												</div>
												<div className="d-flex justify-content-between">
													<div className="text-start">
														<div className="fz-11 text-primary fw-bold">{cashPct}%</div>
														<div className="fz-10 text-muted">{formatMoney(cashAmt)}</div>
													</div>
													<div className="text-end">
														<div className="fz-11 text-danger fw-bold">{creditPct}%</div>
														<div className="fz-10 text-muted">{formatMoney(creditAmt)}</div>
													</div>
												</div>
											</>
										) : <div className="text-muted fw-bold">—</div>}
									</div>
								</div>
							</div>
						</div>

						{/* Row 3: Dates & debt age */}
						<div className="row g-2 mb-2">
							<StatCard label={t('customer_since')} color="text-dark"
								value={card.first_cheque_date ? formatDate(card.first_cheque_date, 'dd.MM.yyyy') : '—'} />
							<StatCard label={t('last_payment_date')}
								color={card.last_payment_date ? 'text-success' : 'text-muted'}
								value={card.last_payment_date ? formatDate(card.last_payment_date, 'dd.MM.yyyy') : '—'} />
							<StatCard label={t('last_credit_date')}
								color={card.last_credit_date ? 'text-danger' : 'text-muted'}
								value={card.last_credit_date ? formatDate(card.last_credit_date, 'dd.MM.yyyy') : '—'} />
							<StatCard label={t('debt_age')} color={debtColor}
								value={debtAge != null ? `${debtAge} ${t('days_short')}` : '—'} />
						</div>

						{/* Row 4: Purchase behavior */}
						<div className="row g-2 mb-3">
							<div className="col-6">
								<div className="card h-100">
									<div className="card-body text-center py-3 px-2">
										<div className="text-muted fz-11 mb-1">{t('purchase_frequency')}</div>
										<div className="fw-bold fs-6 text-secondary">
											{card.avg_purchase_gap_days != null
												? `${t('every')} ${card.avg_purchase_gap_days} ${t('days_short')}`
												: '—'}
										</div>
									</div>
								</div>
							</div>
							<div className="col-6">
								<div className="card h-100">
									<div className="card-body text-center py-3 px-2">
										<div className="text-muted fz-11 mb-1">{t('most_active_month')}</div>
										<div className="fw-bold fs-6 text-primary">
											{card.most_active_month != null
												? t(`month_${card.most_active_month}`)
												: '—'}
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)
			})()}

			{/* Unified history */}
			<div className={`card ${modal ? 'modal-page' : ''}`}>
				<div className="card-body">

					{history.total > 0 &&
						<div className="text-muted fz-13 mb-3">
							{t('total')}: <span className="fw-bold ms-1">{history.total}</span>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '40px' }}>#</th>
									<th style={{ minWidth: '80px' }}>{t('cheque')} №</th>
									<th style={{ minWidth: '140px' }}>{t('date')}</th>
									<th style={{ minWidth: '130px' }}>{t('type')}</th>
									<th style={{ minWidth: '110px' }}>{t('cashier')}</th>
									<th style={{ minWidth: '100px' }}>{t('payment_type')}</th>
									<th style={{ minWidth: '130px' }} className="text-end">{t('sale_sum')}</th>
									<th style={{ minWidth: '120px' }} className="text-end">{t('given')}</th>
									<th style={{ minWidth: '120px' }} className="text-end">{t('debt')}</th>
									<th style={{ minWidth: '40px' }}></th>
								</tr>
							</thead>
							<tbody>
								{history.data?.map((item, index) => {
									const op = getRowLabel(item)
									return (
										<tr key={index} className="tr-middle">
											<td>{item.row_number}</td>
											<td className="text-secondary">
												{item.cheque_id ? `#${item.cheque_id}` : '—'}
											</td>
											<td>{item.event_date ? formatDate(item.event_date, 'dd.MM.yyyy HH:mm') : '—'}</td>
											<td>
												<span className="badge" style={{ backgroundColor: op.bg, color: op.color }}>
													{op.label}
												</span>
											</td>
											<td>{item.cashier_login || '—'}</td>
											<td className="text-muted fz-13">{item.payment_type_name || '—'}</td>
											<td className="text-end text-nowrap">
												{getAmountCell(item)}
											</td>
											<td className="text-end text-nowrap">
												{(() => {
													// "Берилган": qarz qaytarildi qatorida summa item.amount da, boshqalarda item.paid da
													const given = item.row_type === 'payment' ? item.amount : item.paid
													return given != null && Number(given) > 0
														? <span className="text-success">{formatMoney(given)}</span>
														: <span className="text-muted">—</span>
												})()}
											</td>
											<td className="text-end text-nowrap">
												{item.balance != null
													? <span className={Number(item.balance) >= 0 ? 'text-success' : 'text-danger'}>
														{formatMoney(item.balance)}
													</span>
													: <span className="text-muted">—</span>
												}
											</td>
											<td>
												{item.cheque_id &&
													<button className="btn btn-sm btn-outline-secondary"
														onClick={() => toggleRightbar(true, item.cheque_id)}
														title={t('detail')}>
														<i className="uil uil-eye"></i>
													</button>
												}
											</td>
										</tr>
									)
								})}
								{(!history.data || history.data.length === 0) &&
									<tr><td colSpan="10" className="text-center text-muted py-4">{t('list_empty')}</td></tr>
								}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination}
							paginate={(page, size) => setFilter({ ...filter, page, size })} />
					}
				</div>
			</div>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={selectedItem} />
		</>
	)
}

export default Index
